import React, { useState, useEffect, useCallback } from 'react';
import Cookies from 'js-cookie';
import { getApiBase } from './Config';

const PAGE_OPTIONS = [
  { value: 'page_1', label: 'Questionnaire 1' },
  { value: 'page_2', label: 'Questionnaire 2' },
  { value: 'page_3', label: 'Questionnaire 3' },
];

function authHeaders() {
  const t = Cookies.get('access_token');
  return t ? { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' } : {};
}

export default function QuestionnaireTab() {
  const [pageKey, setPageKey] = useState('page_1');
  const [questions, setQuestions] = useState([]);
  const [loadingQ, setLoadingQ] = useState(false);
  const [form, setForm] = useState({
    qtype: 'text',
    label: '',
    required: true,
    display_order: 0,
    active: true,
    choices: [{ id: 'a', text: '', score: '' }, { id: 'b', text: '', score: '' }],
  });
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState(null);

  const [patients, setPatients] = useState({ items: [], total: 0, page: 0 });
  const [loadingP, setLoadingP] = useState(false);
  const [filters, setFilters] = useState({ name: '', user_id: '', page_key: '', date_from: '', date_to: '' });
  const [selPatient, setSelPatient] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [selSubmission, setSelSubmission] = useState(null);
  const [detail, setDetail] = useState(null);
  const [adminMsg, setAdminMsg] = useState('');

  const loadQuestions = useCallback(async () => {
    setLoadingQ(true);
    setError(null);
    try {
      const r = await fetch(
        `${getApiBase()}/admin/questionnaire/questions?page_key=${pageKey}`,
        { headers: authHeaders() }
      );
      if (!r.ok) throw new Error(await r.text());
      setQuestions(await r.json());
    } catch (e) {
      setError(String(e));
    } finally {
      setLoadingQ(false);
    }
  }, [pageKey]);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  const loadPatients = async (page = 0) => {
    setLoadingP(true);
    setError(null);
    try {
      const q = new URLSearchParams();
      q.set('page', String(page));
      q.set('size', '50');
      if (filters.name) q.set('name', filters.name);
      if (filters.user_id) q.set('user_id', filters.user_id);
      if (filters.page_key) q.set('page_key', filters.page_key);
      if (filters.date_from) q.set('date_from', filters.date_from);
      if (filters.date_to) q.set('date_to', filters.date_to);
      const r = await fetch(`${getApiBase()}/admin/questionnaire/patients?${q.toString()}`, {
        headers: authHeaders(),
      });
      if (!r.ok) throw new Error(await r.text());
      setPatients(await r.json());
    } catch (e) {
      setError(String(e));
    } finally {
      setLoadingP(false);
    }
  };

  useEffect(() => {
    loadPatients(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only
  }, []);

  const openPatient = async (pid) => {
    setSelPatient(pid);
    setSelSubmission(null);
    setDetail(null);
    setSubmissions([]);
    const r = await fetch(`${getApiBase()}/admin/questionnaire/users/${pid}/submissions`, {
      headers: authHeaders(),
    });
    if (r.ok) setSubmissions(await r.json());
  };

  const openSubmission = async (id) => {
    setSelSubmission(id);
    setDetail(null);
    setAdminMsg('');
    const r = await fetch(`${getApiBase()}/admin/questionnaire/submissions/${id}`, {
      headers: authHeaders(),
    });
    if (r.ok) {
      const d = await r.json();
      setDetail(d);
      if (d.admin_response && d.admin_response_final) setAdminMsg(d.admin_response);
    }
  };

  const createQuestion = async (e) => {
    e.preventDefault();
    setError(null);
    let choices = null;
    if (form.qtype === 'choice') {
      const prepared = [];
      for (let i = 0; i < form.choices.length; i++) {
        const c = form.choices[i];
        const text = (c.text || '').trim();
        if (!text) continue;
        if (c.score === '' || c.score === null || c.score === undefined) {
          setError(`Choice ${i + 1}: enter a score (0–9)`);
          return;
        }
        const score = Number(c.score);
        if (!Number.isInteger(score) || score < 0 || score > 9) {
          setError(`Choice ${i + 1}: score must be a single digit 0–9`);
          return;
        }
        prepared.push({ id: c.id || String.fromCharCode(97 + prepared.length), text, score });
      }
      if (prepared.length === 0) {
        setError('Add at least one choice with text and score');
        return;
      }
      choices = prepared;
    }
    const body = {
      page_key: pageKey,
      qtype: form.qtype,
      label: form.label,
      required: form.required,
      display_order: Number(form.display_order) || 0,
      active: form.active,
      choices,
    };
    const r = await fetch(`${getApiBase()}/admin/questionnaire/questions`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(body),
    });
    if (!r.ok) {
      setError(await r.text());
      return;
    }
    setForm({
      qtype: 'text',
      label: '',
      required: true,
      display_order: 0,
      active: true,
      choices: [{ id: 'a', text: '', score: '' }, { id: 'b', text: '', score: '' }],
    });
    loadQuestions();
  };

  const saveEdit = async (q) => {
    setError(null);
    let choices = null;
    if (q.qtype === 'choice') {
      const prepared = [];
      const raw = Array.isArray(q.choices) ? q.choices : [];
      for (let i = 0; i < raw.length; i++) {
        const c = raw[i];
        const text = typeof c === 'string' ? c.trim() : String(c?.text || '').trim();
        if (!text) continue;
        const scoreRaw = typeof c === 'object' ? c.score : '';
        if (scoreRaw === '' || scoreRaw === null || scoreRaw === undefined) {
          setError(`Choice ${i + 1}: enter a score (0–9)`);
          return;
        }
        const score = Number(scoreRaw);
        if (!Number.isInteger(score) || score < 0 || score > 9) {
          setError(`Choice ${i + 1}: score must be a single digit 0–9`);
          return;
        }
        prepared.push({
          id: (typeof c === 'object' && c.id) ? c.id : String.fromCharCode(97 + prepared.length),
          text,
          score,
        });
      }
      if (prepared.length === 0) {
        setError('Choice questions need at least one option with text and score');
        return;
      }
      choices = prepared;
    }
    const body = {
      label: q.label,
      required: q.required,
      display_order: q.display_order,
      active: q.active,
      qtype: q.qtype,
      choices,
    };
    const r = await fetch(`${getApiBase()}/admin/questionnaire/questions/${q.id}`, {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(body),
    });
    if (r.ok) {
      setEditingId(null);
      loadQuestions();
    } else setError(await r.text());
  };

  const delQ = async (id) => {
    if (!window.confirm('Delete this question?')) return;
    const r = await fetch(`${getApiBase()}/admin/questionnaire/questions/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    if (r.ok) loadQuestions();
  };

  const sendAdminResponse = async () => {
    if (!detail || !adminMsg.trim()) return;
    if (!window.confirm('Click OK to confirm send. This cannot be undone.')) return;
    const r = await fetch(
      `${getApiBase()}/admin/questionnaire/submissions/${detail.id}/respond`,
      {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ message: adminMsg.trim() }),
      }
    );
    if (r.ok) {
      setDetail((prev) => ({
        ...prev,
        admin_response: adminMsg.trim(),
        admin_response_final: true,
      }));
    } else setError(await r.text());
  };

  const formatAnswerRows = (payload) => {
    const answers = payload?.answers;
    if (!Array.isArray(answers)) return [];
    return answers.map((row, idx) => {
      const q = row?.question ? String(row.question) : `Question ${idx + 1}`;
      const a = row?.answer !== undefined && row?.answer !== null ? String(row.answer) : '—';
      const score =
        row?.score !== undefined && row?.score !== null && row?.score !== ''
          ? Number(row.score)
          : null;
      return { q, a, score: Number.isFinite(score) ? score : null };
    });
  };

  const formatChoiceLabel = (c) => {
    if (typeof c === 'string') return c;
    const text = c?.text || '';
    if (c?.score === undefined || c?.score === null || c?.score === '') return text;
    return `${text} (${c.score} pts)`;
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow space-y-6">
      <div className="space-y-1">
        <h2 className="text-xl font-semibold text-gray-900">Questionnaire</h2>
        <p className="text-sm text-gray-500">Manage forms, patient responses, and admin replies.</p>
      </div>
      {error && <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</p>}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
        <div className="md:col-span-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">Form</label>
        <select
          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          value={pageKey}
          onChange={(e) => setPageKey(e.target.value)}
        >
          {PAGE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        </div>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 space-y-4">
        <h3 className="text-base font-semibold text-gray-900">Add / edit questions</h3>
        {loadingQ && <p className="text-sm text-gray-500">Loading…</p>}
        <form onSubmit={createQuestion} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <select
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              value={form.qtype}
              onChange={(e) => setForm({ ...form, qtype: e.target.value })}
            >
              <option value="text">Text</option>
              <option value="choice">Choice</option>
            </select>
            <input
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              placeholder="Display order"
              type="number"
              value={form.display_order}
              onChange={(e) => setForm({ ...form, display_order: e.target.value })}
            />
          </div>
          <textarea
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            placeholder="Question text"
            rows={2}
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            required
          />
          {form.qtype === 'choice' && (
            <div className="space-y-2">
              <p className="text-xs text-gray-500">Each option needs text and a score (single digit 0–9).</p>
              {form.choices.map((c, i) => (
                <div key={i} className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                  <input
                    className="sm:col-span-3 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    placeholder={`Choice ${i + 1} text`}
                    value={c.text}
                    onChange={(e) => {
                      const n = [...form.choices];
                      n[i] = { ...n[i], text: e.target.value };
                      setForm({ ...form, choices: n });
                    }}
                  />
                  <input
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    placeholder="0–9"
                    type="number"
                    min="0"
                    max="9"
                    step="1"
                    value={c.score}
                    onChange={(e) => {
                      const n = [...form.choices];
                      n[i] = { ...n[i], score: e.target.value };
                      setForm({ ...form, choices: n });
                    }}
                    required
                  />
                </div>
              ))}
              <button
                type="button"
                className="text-sm font-medium text-blue-600 hover:text-blue-700"
                onClick={() => setForm({
                  ...form,
                  choices: [
                    ...form.choices,
                    { id: String.fromCharCode(97 + form.choices.length), text: '', score: '' },
                  ],
                })}
              >
                + Add choice
              </button>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.required}
                onChange={(e) => setForm({ ...form, required: e.target.checked })}
              />
              Required
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
              />
              Active
            </label>
          </div>
          <div className="flex justify-end">
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700">
              Add question
            </button>
          </div>
        </form>

        <ul className="divide-y divide-gray-200 border border-gray-200 rounded-lg bg-white">
          {questions.map((q) => (
            <li key={q.id} className="px-4 py-3">
              {editingId === q.id ? (
                <EditRow q={q} onSave={saveEdit} onCancel={() => setEditingId(null)} />
              ) : (
                <div className="flex flex-wrap justify-between items-start gap-3">
                  <div className="pr-4">
                    <span className="text-xs text-gray-500">#{q.display_order}</span> {q.label}{' '}
                    <span className="text-xs">({q.qtype})</span>
                    {!q.active && <span className="text-xs text-amber-600"> [inactive]</span>}
                    {q.qtype === 'choice' && Array.isArray(q.choices) && q.choices.length > 0 && (
                      <ul className="mt-1 text-xs text-gray-600 list-disc list-inside">
                        {q.choices.map((c, i) => (
                          <li key={c.id || i}>{formatChoiceLabel(c)}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="space-x-3">
                    <button type="button" className="text-sm font-medium text-blue-600 hover:text-blue-700" onClick={() => setEditingId(q.id)}>
                      Edit
                    </button>
                    <button type="button" className="text-sm font-medium text-red-600 hover:text-red-700" onClick={() => delQ(q.id)}>
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 space-y-4">
        <h3 className="text-base font-semibold text-gray-900">Patients (most recent first, 50 per page)</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-3">
          <input
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            placeholder="Name / phone"
            value={filters.name}
            onChange={(e) => setFilters({ ...filters, name: e.target.value })}
          />
          <input
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            placeholder="User ID"
            value={filters.user_id}
            onChange={(e) => setFilters({ ...filters, user_id: e.target.value })}
          />
          <select
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            value={filters.page_key}
            onChange={(e) => setFilters({ ...filters, page_key: e.target.value })}
          >
            <option value="">All pages</option>
            {PAGE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <input
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            type="date"
            value={filters.date_from}
            onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
          />
          <input
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            type="date"
            value={filters.date_to}
            onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
          />
          <button
            type="button"
            className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
            onClick={() => loadPatients(0)}
          >
            Search
          </button>
        </div>
        {loadingP && <p className="text-sm text-gray-500">Loading…</p>}
        <div className="overflow-x-auto border border-gray-200 rounded-lg bg-white">
          <table className="min-w-full text-sm divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr className="text-left">
                <th className="px-4 py-3 text-xs font-medium uppercase tracking-wide text-gray-600">Patient</th>
                <th className="px-4 py-3 text-xs font-medium uppercase tracking-wide text-gray-600">ID</th>
                <th className="px-4 py-3 text-xs font-medium uppercase tracking-wide text-gray-600">Last answered</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {patients.items?.map((p) => (
                <tr
                  key={p.patient_id}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => openPatient(p.patient_id)}
                >
                  <td className="px-4 py-3">{p.name}</td>
                  <td className="px-4 py-3">{p.patient_id}</td>
                  <td className="px-4 py-3">{p.last_answered_at}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={patients.page <= 0}
            className="px-3 py-1.5 bg-gray-200 rounded-md text-sm disabled:opacity-50"
            onClick={() => loadPatients(patients.page - 1)}
          >
            Prev
          </button>
          <span className="text-sm text-gray-600">
            Page {patients.page + 1} / {Math.max(1, Math.ceil((patients.total || 0) / 50))} (total {patients.total})
          </span>
          <button
            type="button"
            className="px-3 py-1.5 bg-gray-200 rounded-md text-sm"
            onClick={() => loadPatients(patients.page + 1)}
          >
            Next
          </button>
        </div>
      </div>

      {selPatient && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-base font-semibold text-gray-900">Submissions for patient {selPatient}</h3>
            <button type="button" className="text-sm font-medium text-gray-600 hover:text-gray-800" onClick={() => { setSelPatient(null); setSubmissions([]); setSelSubmission(null); setDetail(null); }}>
              Close
            </button>
          </div>
          <ul className="divide-y divide-gray-200 border border-gray-200 rounded-lg bg-white">
            {submissions.map((s) => (
              <li key={s.id} className="px-4 py-3">
                <button
                  type="button"
                  className="text-blue-600 hover:text-blue-700 text-left text-sm"
                  onClick={() => openSubmission(s.id)}
                >
                  {s.page_key} — {s.answered_at}
                  {s.recording_context ? ` (ctx: ${s.recording_context})` : ''}
                  {s.total_score !== undefined && s.total_score !== null
                    ? ` · Total score: ${s.total_score}`
                    : ''}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {detail && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 space-y-4">
          <h3 className="text-base font-semibold text-gray-900">Submission #{detail.id}</h3>
          <p className="text-sm text-gray-600">
            {detail.patient_name} ({detail.patient_email})
          </p>
          <div className="bg-white border border-gray-200 rounded-md p-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-xs uppercase tracking-wide text-gray-500 font-medium">
                Questionnaire answers
              </div>
              {(detail.total_score !== undefined && detail.total_score !== null) ||
              (detail.payload && detail.payload.total_score !== undefined && detail.payload.total_score !== null) ? (
                <div className="text-sm font-semibold text-gray-900">
                  Total score:{' '}
                  {detail.total_score !== undefined && detail.total_score !== null
                    ? detail.total_score
                    : detail.payload.total_score}
                </div>
              ) : null}
            </div>
            {formatAnswerRows(detail.payload).length > 0 ? (
              <div className="space-y-3">
                {formatAnswerRows(detail.payload).map((row, idx) => (
                  <div key={`${idx}_${row.q}`} className="border border-gray-100 rounded-md p-3 bg-gray-50">
                    <p className="text-sm font-medium text-gray-800">{idx + 1}. {row.q}</p>
                    <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{row.a}</p>
                    {row.score !== null && (
                      <p className="text-xs text-gray-500 mt-1">Score: {row.score}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No answers captured in this submission.</p>
            )}
          </div>
          {detail.admin_response_final ? (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-800">Admin response (sent):</p>
              <p className="whitespace-pre-wrap text-sm border border-gray-200 bg-white p-3 rounded-md">{detail.admin_response}</p>
            </div>
          ) : (
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">Your response</label>
              <textarea
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                rows={4}
                value={adminMsg}
                onChange={(e) => setAdminMsg(e.target.value)}
                placeholder="Type response for the patient"
              />
              <div className="flex justify-end pt-1">
                <button
                  type="button"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!adminMsg.trim()}
                  onClick={sendAdminResponse}
                >
                  Send response
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function EditRow({ q, onSave, onCancel }) {
  const [qtype, setQtype] = useState(q.qtype);
  const [label, setLabel] = useState(q.label);
  const [required, setRequired] = useState(q.required);
  const [display_order, setDisplayOrder] = useState(q.display_order);
  const [active, setActive] = useState(q.active);
  const [choices, setChoices] = useState(
    (q.choices && q.choices.length
      ? q.choices.map((c, i) =>
          typeof c === 'string'
            ? { id: String.fromCharCode(97 + i), text: c, score: '' }
            : { id: c.id || String.fromCharCode(97 + i), text: c.text || '', score: c.score ?? '' }
        )
      : [{ id: 'a', text: '', score: '' }])
  );
  return (
    <div className="space-y-3 border border-gray-200 bg-gray-50 p-3 rounded-md">
      <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={qtype} onChange={(e) => setQtype(e.target.value)}>
        <option value="text">Text</option>
        <option value="choice">Choice</option>
      </select>
      <textarea className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={label} onChange={(e) => setLabel(e.target.value)} />
      <input
        type="number"
        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
        value={display_order}
        onChange={(e) => setDisplayOrder(Number(e.target.value))}
      />
      {qtype === 'choice' && (
        <div className="space-y-2">
          <p className="text-xs text-gray-500">Each option needs text and a score (single digit 0–9).</p>
          {choices.map((c, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-4 gap-2">
              <input
                className="sm:col-span-3 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                value={c.text || ''}
                placeholder={`Choice ${i + 1} text`}
                onChange={(e) => {
                  const n = [...choices];
                  n[i] = { ...n[i], text: e.target.value };
                  setChoices(n);
                }}
              />
              <input
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                type="number"
                min="0"
                max="9"
                step="1"
                placeholder="0–9"
                value={c.score}
                onChange={(e) => {
                  const n = [...choices];
                  n[i] = { ...n[i], score: e.target.value };
                  setChoices(n);
                }}
              />
            </div>
          ))}
          <button
            type="button"
            className="text-sm font-medium text-blue-600 hover:text-blue-700"
            onClick={() =>
              setChoices([
                ...choices,
                { id: String.fromCharCode(97 + choices.length), text: '', score: '' },
              ])
            }
          >
            + Add choice
          </button>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="flex items-center gap-1 text-sm text-gray-700">
          <input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} /> Required
        </label>
        <label className="flex items-center gap-1 text-sm text-gray-700">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Active
        </label>
      </div>
      <div className="space-x-2">
        <button
          type="button"
          className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
          onClick={() => onSave({ ...q, qtype, label, required, display_order, active, choices: qtype === 'choice' ? choices : null })}
        >
          Save
        </button>
        <button type="button" className="px-3 py-1.5 bg-gray-200 rounded-md text-sm" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
